// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.22.0 (decisions 7–8) — what the global Forseti launcher knows about
 * the page it was opened on.
 *
 * Pages REGISTER; the launcher READS. A view that shows one record calls
 * `useRegisterForsetiRecord({ type, id, label })`, which registers on mount and
 * unregisters on unmount. The latest registration wins, so a drawer opened over
 * a detail page describes the drawer's record, and closing it hands the page's
 * record back. A list page registers nothing, which is what makes "nothing is
 * pre-selected on a list page" a property of the pages rather than a rule the
 * launcher has to guess.
 *
 * `AdvisorRunPanel` — the component detail views mount — registers through
 * this hook, so most detail pages gain it with no edit. The row-level
 * `AdvisorRunButton` deliberately does not: a list of fifty rows is not a page
 * about any one of them. `forseti-page-context-census-63.22.0.test.ts` holds
 * the detail routes the panel does not reach.
 *
 * Outside a provider the hook is a no-op, so a page rendered on its own in a
 * test needs no wrapper.
 *
 * Registered in REUSE.md ("Current page record for Forseti"): no provider
 * published the current record before this; each page passed its type and id
 * to its own Forseti button.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ParentRecordType } from '@vn-grc/shared';

export interface ForsetiPageRecord {
  type: ParentRecordType;
  id: string;
  /**
   * What the launchpad calls the record ("Ask about this {label}"). Optional:
   * without it the launcher uses the record type's own label, which every
   * `ParentRecordType` has.
   */
  label?: string;
}

interface Registration {
  token: number;
  record: ForsetiPageRecord;
}

interface ForsetiPageRegistry {
  register: (token: number, record: ForsetiPageRecord) => void;
  unregister: (token: number) => void;
  nextToken: () => number;
}

const RegistryContext = createContext<ForsetiPageRegistry | null>(null);
const CurrentRecordContext = createContext<ForsetiPageRecord | null>(null);

export function ForsetiPageProvider({ children }: { children: ReactNode }): JSX.Element {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const counterRef = useRef(0);

  const register = useCallback((token: number, record: ForsetiPageRecord) => {
    // Re-registering moves the entry to the end: the most recent registration
    // is the one the operator is looking at.
    setRegistrations((prev) => [...prev.filter((r) => r.token !== token), { token, record }]);
  }, []);

  const unregister = useCallback((token: number) => {
    setRegistrations((prev) =>
      prev.some((r) => r.token === token) ? prev.filter((r) => r.token !== token) : prev,
    );
  }, []);

  const nextToken = useCallback(() => {
    counterRef.current += 1;
    return counterRef.current;
  }, []);

  const registry = useMemo<ForsetiPageRegistry>(
    () => ({ register, unregister, nextToken }),
    [register, unregister, nextToken],
  );

  const current = registrations.length > 0 ? registrations[registrations.length - 1]!.record : null;

  return (
    <RegistryContext.Provider value={registry}>
      <CurrentRecordContext.Provider value={current}>{children}</CurrentRecordContext.Provider>
    </RegistryContext.Provider>
  );
}

/**
 * Register the record this view shows. Pass `null` (or an empty id) while the
 * record is still loading or was not found — registering an id the page could
 * not read would offer the operator a chat about nothing.
 */
export function useRegisterForsetiRecord(record: ForsetiPageRecord | null | undefined): void {
  const registry = useContext(RegistryContext);
  const tokenRef = useRef<number | null>(null);
  const type = record?.type;
  const id = record?.id;
  const label = record?.label;

  useEffect(() => {
    if (!registry || !type || !id) return undefined;
    if (tokenRef.current === null) tokenRef.current = registry.nextToken();
    const token = tokenRef.current;
    registry.register(token, { type, id, ...(label ? { label } : {}) });
    return () => registry.unregister(token);
  }, [registry, type, id, label]);
}

/** The record the current page registered, or `null` (a list page, or no provider). */
export function useForsetiPageRecord(): ForsetiPageRecord | null {
  return useContext(CurrentRecordContext);
}
